'use client';

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { Input, Button, message, Spin } from 'antd';
import { EnvironmentOutlined, AimOutlined } from '@ant-design/icons';

interface LocationPickerProps {
  latitude?: string;
  longitude?: string;
  onChange: (coords: { latitude: string; longitude: string }) => void;
  onAddressFound?: (address: {
    street?: string;
    town?: string;
    state?: string;
    postcode?: string;
  }) => void;
}

const GOOGLE_MAPS_API_KEY =
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || 'AIzaSyAd6J-M2idmQ_dBi3tfD8CGPws_cpYvDEM';

// Australia centre
const DEFAULT_CENTER = { lat: -25.2744, lng: 133.7751 };
const DEFAULT_ZOOM = 4;

const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  onChange,
  onAddressFound,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [coordInput, setCoordInput] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);

  // Reverse geocoding via Nominatim (free, same as admin GPSInput)
  const reverseGeocode = useCallback(
    async (lat: string, lng: string) => {
      if (!onAddressFound) return;
      setIsLookingUp(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        );
        if (res.ok) {
          const data = await res.json();
          const addr = data.address || {};
          const mapped = {
            street:
              addr.house_number && addr.road
                ? `${addr.house_number} ${addr.road}`
                : addr.road || addr.street || '',
            town: addr.city || addr.town || addr.suburb || addr.village || '',
            state: addr.state || '',
            postcode: addr.postcode || '',
          };
          if (mapped.town || mapped.street) {
            onAddressFound(mapped);
            message.success('Address auto-filled from map pin');
          }
        }
      } catch {
        // silent fail
      } finally {
        setIsLookingUp(false);
      }
    },
    [onAddressFound],
  );

  // Place or move the marker
  const placeMarker = useCallback(
    (lat: number, lng: number) => {
      const map = mapInstanceRef.current;
      if (!map) return;

      if (markerRef.current) {
        markerRef.current.position = { lat, lng };
      } else {
        const marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat, lng },
          gmpDraggable: true,
          title: 'Campsite location',
        });

        marker.addListener('dragend', () => {
          const pos = marker.position as google.maps.LatLngLiteral;
          const newLat = pos.lat.toFixed(6);
          const newLng = pos.lng.toFixed(6);
          onChange({ latitude: newLat, longitude: newLng });
          setCoordInput(`${newLat}, ${newLng}`);
          reverseGeocode(newLat, newLng);
        });

        markerRef.current = marker;
      }

      map.panTo({ lat, lng });
      if (map.getZoom()! < 12) map.setZoom(14);
    },
    [onChange, reverseGeocode],
  );

  // Init Google Map
  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;

    const loader = new Loader({
      apiKey: GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['marker'],
    });

    loader
      .importLibrary('maps')
      .then(({ Map }) => {
        if (cancelled || !mapRef.current) return;

        const center =
          latitude && longitude
            ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
            : DEFAULT_CENTER;

        const map = new Map(mapRef.current, {
          center,
          zoom: latitude && longitude ? 14 : DEFAULT_ZOOM,
          mapId: 'submission-location-picker',
          gestureHandling: 'greedy',
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        });

        mapInstanceRef.current = map;
        setMapReady(true);

        // Click to place pin
        map.addListener('click', (e: google.maps.MapMouseEvent) => {
          if (!e.latLng) return;
          const lat = e.latLng.lat().toFixed(6);
          const lng = e.latLng.lng().toFixed(6);
          onChange({ latitude: lat, longitude: lng });
          setCoordInput(`${lat}, ${lng}`);
          reverseGeocode(lat, lng);
        });

        // If we already have coords, place pin
        if (latitude && longitude) {
          const lat = parseFloat(latitude);
          const lng = parseFloat(longitude);
          if (!isNaN(lat) && !isNaN(lng)) {
            // Delay to ensure AdvancedMarkerElement is available
            loader.importLibrary('marker').then(() => {
              placeMarker(lat, lng);
            });
          }
        }
      })
      .catch((err) => console.error('Failed to load Google Maps:', err));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update marker when coords change externally
  useEffect(() => {
    if (!mapReady) return;
    if (latitude && longitude) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        placeMarker(lat, lng);
        setCoordInput(`${latitude}, ${longitude}`);
      }
    }
  }, [latitude, longitude, mapReady, placeMarker]);

  // Manual coord input
  const handleCoordInput = (value: string) => {
    setCoordInput(value);
    const match = value.trim().match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
    if (match) {
      const lat = match[1];
      const lng = match[2];
      onChange({ latitude: lat, longitude: lng });
      reverseGeocode(lat, lng);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input
          prefix={<EnvironmentOutlined className="text-gray-400" />}
          placeholder="Click the map or paste GPS: -32.111, 115.764"
          value={coordInput}
          onChange={(e) => handleCoordInput(e.target.value)}
          suffix={isLookingUp ? <Spin size="small" /> : undefined}
          className="flex-1"
        />
        {latitude && longitude && (
          <Button
            icon={<AimOutlined />}
            onClick={() => {
              const map = mapInstanceRef.current;
              if (map) {
                map.panTo({
                  lat: parseFloat(latitude),
                  lng: parseFloat(longitude),
                });
                map.setZoom(14);
              }
            }}
            title="Centre on pin"
          />
        )}
      </div>

      <div
        ref={mapRef}
        className="w-full h-[350px] rounded-xl border border-gray-200 bg-gray-50"
      />

      <p className="text-xs text-gray-400">
        Click anywhere on the map to drop a pin, or paste GPS coordinates above.
        Drag the pin to adjust.
      </p>
    </div>
  );
};

export default LocationPicker;
